import { useEffect } from "react";
import {
  AutocompleteInput,
  Create,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  Filter,
  List,
  Pagination,
  ReferenceField,
  ReferenceInput,
  Resource,
  SelectInput,
  Show,
  SimpleForm,
  TextField,
  TextInput,
  useListContext
} from "react-admin";


// Generic filters (for backwards compatibility)
const Filters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="material.name" alwaysOn />
    <ReferenceInput label="Material" source="materialId" reference="material">
      <SelectInput optionText="materialName" />
    </ReferenceInput>
    <ReferenceInput label="Process" source="processId" reference="process">
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
      <SelectInput optionText="supplierName" />
    </ReferenceInput>
    <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })} >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Transport" source="transportId" reference="transport">
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

// Specific filters for Material Extraction
const MaterialExtractionFilters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="material.name" alwaysOn />
    <ReferenceInput label="Project" source="projectId" reference="projects" perPage={500} alwaysOn>
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Material" source="materialId" reference="material">
      <SelectInput optionText="materialName" />
    </ReferenceInput>
    <ReferenceInput label="Extraction" source="extractionId" reference="extraction">
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
      <SelectInput optionText="supplierName" />
    </ReferenceInput>
    <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })} >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Transport" source="transportId" reference="transport">
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

// Specific filters for Material Processing
const MaterialProcessingFilters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="material.name" alwaysOn />
    <ReferenceInput label="Project" source="projectId" reference="projects" perPage={500} alwaysOn>
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Material" source="materialId" reference="material">
      <SelectInput optionText="materialName" />
    </ReferenceInput>
    <ReferenceInput label="Processing" source="processingId" reference="processing">
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
      <SelectInput optionText="supplierName" />
    </ReferenceInput>
    <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })} >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Transport" source="transportId" reference="transport">
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

// Specific filters for Material Finishing
const MaterialFinishingFilters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="material.name" alwaysOn />
    <ReferenceInput label="Project" source="projectId" reference="projects" perPage={500} alwaysOn>
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Material" source="materialId" reference="material">
      <SelectInput optionText="materialName" />
    </ReferenceInput>
    <ReferenceInput label="Finishing" source="finishingId" reference="finishing">
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
      <SelectInput optionText="supplierName" />
    </ReferenceInput>
    <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })} >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Transport" source="transportId" reference="transport">
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const SubReference = ({ children, ...props }) => {
  const { record } = props;
  return <ReferenceField {...props} source="categoryId" reference="lcaCategory" filter={{ id: record.categoryId }}>{children}</ReferenceField>
}

// Custom pagination component for debugging
const CustomPagination = (props) => {
  const { page, perPage, total, setPage, setPerPage } = useListContext();

  useEffect(() => {
    console.log('Pagination data:', { page, perPage, total, hasSetPage: !!setPage, hasSetPerPage: !!setPerPage });
  }, [page, perPage, total, setPage, setPerPage]);

  return <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;
};

// Material Extraction components
const MaterialExtractionList = (props) => {
  return (
    <List
      {...props}
      resource="materialExtraction"
      filters={<MaterialExtractionFilters />}
      pagination={<CustomPagination />}
      bulkActionButtons={false}
    >
      <Datagrid rowClick="show">
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        {/* IT-3172: requiere que el backend embeba `project: { id, name }` en el registro */}
        <TextField source="project.name" label="Project" />
        <TextField source="material.name" label="Material" />
        <ReferenceField label="Extraction" source="extractionId" reference="extraction" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="supplier.name" label="Supplier" />
        <ReferenceField label="Origin" source="originId" reference="origins" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Transport" source="transportId" reference="transport" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="relativeAmount" />
        <TextField source="order" />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const ShowMaterialExtraction = (props) => {
  return (
    <Show {...props} resource="materialExtraction">
      <SimpleForm>
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="material.name" label="Material" />
        <ReferenceField label="Extraction" source="extractionId" reference="extraction">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="supplier.name" label="Supplier" />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Transport" source="transportId" reference="transport">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="relativeAmount" />
        <TextField source="order" />
      </SimpleForm>
    </Show>
  );
};

const CreateMaterialExtraction = (props) => {
  return (
    <Create {...props} resource="materialExtraction">
      <SimpleForm>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="materialName" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Extraction" source="extractionId" reference="extraction">
          <SelectInput optionText="name" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
          <SelectInput optionText="supplierName" required={true} />
        </ReferenceInput>
        <TextInput source="relativeAmount" />
        <TextInput source="order" />
      </SimpleForm>
    </Create>
  );
};

const EditMaterialExtraction = (props) => {
  return (
    <Edit {...props} resource="materialExtraction">
      <SimpleForm>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="materialName" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Extraction" source="extractionId" reference="extraction">
          <SelectInput optionText="name" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
          <SelectInput optionText="supplierName" required={true} />
        </ReferenceInput>
        <TextInput source="relativeAmount" />
        <TextInput source="order" />
      </SimpleForm>
    </Edit>
  );
};

// Material Processing components
const MaterialProcessingList = (props) => {
  return (
    <List
      {...props}
      resource="materialProcessing"
      filters={<MaterialProcessingFilters />}
      pagination={<CustomPagination />}
      bulkActionButtons={false}
    >
      <Datagrid rowClick="show">
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        {/* IT-3172: requiere que el backend embeba `project: { id, name }` en el registro */}
        <TextField source="project.name" label="Project" />
        <TextField source="material.name" label="Material" />
        <ReferenceField label="Processing" source="processingId" reference="processing" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="supplier.name" label="Supplier" />
        <ReferenceField label="Origin" source="originId" reference="origins" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Transport" source="transportId" reference="transport" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="relativeAmount" />
        <TextField source="order" />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const ShowMaterialProcessing = (props) => {
  return (
    <Show {...props} resource="materialProcessing">
      <SimpleForm>
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="material.name" label="Material" />
        <ReferenceField label="Processing" source="processingId" reference="processing">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="supplier.name" label="Supplier" />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Transport" source="transportId" reference="transport">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="relativeAmount" />
        <TextField source="order" />
      </SimpleForm>
    </Show>
  );
};

const CreateMaterialProcessing = (props) => {
  return (
    <Create {...props} resource="materialProcessing">
      <SimpleForm>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="materialName" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Processing" source="processingId" reference="processing">
          <SelectInput optionText="name" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
          <SelectInput optionText="supplierName" required={true} />
        </ReferenceInput>
        <TextInput source="relativeAmount" />
        <TextInput source="order" />
      </SimpleForm>
    </Create>
  );
};

const EditMaterialProcessing = (props) => {
  return (
    <Edit {...props} resource="materialProcessing">
      <SimpleForm>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="materialName" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Processing" source="processingId" reference="processing">
          <SelectInput optionText="name" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
          <SelectInput optionText="supplierName" required={true} />
        </ReferenceInput>
        <TextInput source="relativeAmount" />
        <TextInput source="order" />
      </SimpleForm>
    </Edit>
  );
};

// Material Finishing components
const MaterialFinishingList = (props) => {
  return (
    <List
      {...props}
      resource="materialFinishing"
      filters={<MaterialFinishingFilters />}
      pagination={<CustomPagination />}
      bulkActionButtons={false}
    >
      <Datagrid rowClick="show">
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        {/* IT-3172: requiere que el backend embeba `project: { id, name }` en el registro */}
        <TextField source="project.name" label="Project" />
        <TextField source="material.name" label="Material" />
        <ReferenceField label="Finishing" source="finishingId" reference="finishing" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="supplier.name" label="Supplier" />
        <ReferenceField label="Origin" source="originId" reference="origins" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Transport" source="transportId" reference="transport" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <EditButton />
      </Datagrid>
    </List>
  );
};

const ShowMaterialFinishing = (props) => {
  return (
    <Show {...props} resource="materialFinishing">
      <SimpleForm>
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="material.name" label="Material" />
        <ReferenceField label="Finishing" source="finishingId" reference="finishing">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="supplier.name" label="Supplier" />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Transport" source="transportId" reference="transport">
          <TextField source="name" />
        </ReferenceField>
      </SimpleForm>
    </Show>
  );
};

const CreateMaterialFinishing = (props) => {
  return (
    <Create {...props} resource="materialFinishing">
      <SimpleForm>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="materialName" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Finishing" source="finishingId" reference="finishing">
          <SelectInput optionText="name" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
          <SelectInput optionText="supplierName" required={true} />
        </ReferenceInput>
      </SimpleForm>
    </Create>
  );
};

const EditMaterialFinishing = (props) => {
  return (
    <Edit {...props} resource="materialFinishing">
      <SimpleForm>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="materialName" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Finishing" source="finishingId" reference="finishing">
          <SelectInput optionText="name" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
          <SelectInput optionText="supplierName" required={true} />
        </ReferenceInput>
      </SimpleForm>
    </Edit>
  );
};

// Original MaterialProcess components
const MaterialProcessList = (props) => {
  return (
    <List {...props} filters={<Filters />}>
      <div style={{ marginTop: '15px' }}>
        <Datagrid rowClick="show">
          <TextField source="id" />
          <DateField source="createdAt" label="Creation date" />
          <DateField source="updatedAt" label="Update date" />
          <ReferenceField label="Material" source="materialId" reference="material">
            <TextField source="materialName" />
          </ReferenceField>
          <ReferenceField label="Process" source="processId" reference="process">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField label="Category" source="processId" reference="process">
            <SubReference source="process.categoryId" reference="lcaCategory">
              <TextField source="name" />
            </SubReference>
          </ReferenceField>
          <ReferenceField label="Supplier" source="supplierId" reference="supplier">
            <TextField source="supplierName" />
          </ReferenceField>
          <ReferenceField label="Origin" source="originId" reference="origins">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField label="Transport" source="transportId" reference="transport">
            <TextField source="name" />
          </ReferenceField>
          <TextField source="relativeAmount" />
          <TextField source="order" />
          <ReferenceField label="Account" source="accountId" reference="accounts">
            <TextField source="name" />
          </ReferenceField>
          <EditButton />
        </Datagrid>
      </div>
    </List>
  );
};

const ShowMaterialProcess = (props) => {
  return (
    <Show {...props}>
      <SimpleForm>
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <ReferenceField label="Material" source="materialId" reference="material">
          <TextField source="materialName" />
        </ReferenceField>
        <ReferenceField label="Process" source="processId" reference="process">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Category" source="processId" reference="process">
          <SubReference source="process.categoryId" reference="lcaCategory">
            <TextField source="name" />
          </SubReference>
        </ReferenceField>
        <ReferenceField label="Supplier" source="supplierId" reference="supplier">
          <TextField source="supplierName" />
        </ReferenceField>
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Transport" source="transportId" reference="transport">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="relativeAmount" />
        <TextField source="order" />
        <ReferenceField label="Account" source="accountId" reference="accounts">
          <TextField source="name" />
        </ReferenceField>
      </SimpleForm>
    </Show>
  );
};

const CreateMaterialProcess = (props) => {
  return (
    <Create {...props}>
      <SimpleForm>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="materialName" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Process" source="processId" reference="process">
          <SelectInput optionText="name" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
          <SelectInput optionText="supplierName" required={true} />
        </ReferenceInput>
        <TextInput source="relativeAmount" />
        <TextInput source="order" />
      </SimpleForm>
    </Create>
  );
};

const EditMaterialProcess = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="materialName" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Process" source="processId" reference="process">
          <SelectInput optionText="name" required={true} />
        </ReferenceInput>
        <ReferenceInput label="Supplier" source="supplierId" reference="supplier">
          <SelectInput optionText="supplierName" required={true} />
        </ReferenceInput>
        <TextInput source="relativeAmount" />
        <TextInput source="order" />
      </SimpleForm>
    </Edit>
  );
};

// Individual resources for each material process type
const materialExtractionResource = () => <Resource
  name="materialExtraction"
  options={{ label: 'Material extraction', menuGroup: 'Materials' }}
  list={MaterialExtractionList}
  create={CreateMaterialExtraction}
  edit={EditMaterialExtraction}
  show={ShowMaterialExtraction} />;

const materialProcessingResource = () => <Resource
  name="materialProcessing"
  options={{ label: 'Material processing', menuGroup: 'Materials' }}
  list={MaterialProcessingList}
  create={CreateMaterialProcessing}
  edit={EditMaterialProcessing}
  show={ShowMaterialProcessing} />;

const materialFinishingResource = () => <Resource
  name="materialFinishing"
  options={{ label: 'Material finishing', menuGroup: 'Materials' }}
  list={MaterialFinishingList}
  create={CreateMaterialFinishing}
  edit={EditMaterialFinishing}
  show={ShowMaterialFinishing} />;

// Original materialProcess resource (keeping for compatibility)
const materialProcessResource = () => <Resource
  name="materialProcess"
  options={{ label: 'Material processes', menuGroup: 'Materials' }}
  list={MaterialProcessList}
  create={CreateMaterialProcess}
  edit={EditMaterialProcess}
  show={ShowMaterialProcess} />;

export {
  materialExtractionResource, materialFinishingResource, materialProcessingResource
};

export default materialProcessResource;
